import { useMemo } from 'react';
import { CircularProgress, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { get } from 'lodash';

import { BaseNote, NOTE_ACTIONS, StaffNote } from './Types';
import { convertToStoreLocalTime } from '@/common/utils/time';
import { PinIcon } from '@/common/icons/PinIcon';
import { DDDownIcon } from '@/common/icons/DDDownIcon';
import { PinIconSet } from '@/common/icons/PinIconSet';
import { DDUpIcon } from '@/common/icons/DDUpIcon';
import NoteItemMenu from './NoteItemMenu';
import NoteComments from './NoteComments';
import { useCustomerNoteEnums } from '@/hooks/useCustomerNoteEnums';
import moment from 'moment';

const useStyles: any = makeStyles(() => ({
  container: {
    color: 'grey',
    border: '1px solid #E0E0E0',
    padding: '16px 12px',
    marginBottom: 12,
    borderRadius: 8
  },
  saveDetails: {
    color: '#666',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1
  },
  title: {
    color: '#000',
    marginTop: 5
  },
  noteProperties: {
    marginTop: 12,
    padding: '8px 12px',
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    fontSize: 12
  },
  notePropertyName: {
    color: '#666',
    fontWeight: 400
  },
  notePropertyValue: {
    color: '#000',
    fontWeight: 501,
    whiteSpace: 'nowrap',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  controlContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    paddingLeft: 12
  },
  controls: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    height: 42,
    cursor: 'pointer',
    marginTop: 6,
    paddingRight: 10,
    '& > div': {
      marginRight: 8
    }
  },
  pinLoading: {
    '& span': {
      color: '#000',
      width: '20px !important',
      height: '20px !important'
    }
  },
  content: {
    fontWeight: 400,
    margin: '8px 16px 4px 0',
    color: '#000',
    overflow: 'hidden'
  },
  collapsed: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 'calc(70vw - 200px)',
    display: '-webkit-box',
    width: '100%',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical'
  },
  addBtn: {
    color: '#055577',
    paddingLeft: 0,
    marginTop: 8,
    width: 140,
    fontWeight: 600,
    cursor: 'pointer'
  },
  saveCancel: {
    marginTop: 12,
    display: 'flex',
    justifyContent: 'end',
    '& button:first-child': {
      backgroundColor: '#FAFAFA',
      borderColor: '#FAFAFA',
      color: '#000',
      marginRight: 8,
      '&:hover': {
        backgroundColor: '#FFF'
      }
    },
    '& button': {
      backgroundColor: '#055577',
      color: '#FFF',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: '#042E43'
      }
    }
  },
  expandCollapse: {},
  pin: {}
}));

export const getSaveDetails = (note: BaseNote) =>
  `${convertToStoreLocalTime(
    note.createdAt,
    'MM/DD/yyyy'
  )} at ${convertToStoreLocalTime(note.createdAt, 'h:mm A')}`;

export const getNoteCategory = (note: BaseNote, categories) => {
  const category = categories.find(c => c.id === note.categoryId);
  return category ? category.name : '';
};

export interface NoteItemProps {
  note: StaffNote;
  actionCallback: (note: StaffNote, action: keyof typeof NOTE_ACTIONS) => void;
}

export const NoteItem = (props: NoteItemProps) => {
  const { note, actionCallback } = props;
  const { enums, loading } = useCustomerNoteEnums();

  const classes = useStyles();
  const saveDetails = useMemo(() => getSaveDetails(note), [note]);
  const noteCategory = useMemo(() => {
    if (enums) {
      return getNoteCategory(note, enums.noteCategories);
    } else {
      return [];
    }
  }, [note.categoryId, loading]);

  const followUpTime = useMemo(() => {
    if (enums) {
      return get(
        enums.followupTimes,
        `[${note?.followupTime}].description`,
        '--'
      );
    } else {
      return [];
    }
  }, [enums]);

  return (
    <Grid container className={classes.container}>
      <Grid xs={8} item>
        <div className={classes.saveDetails}>{saveDetails}</div>
        <div className={classes.title}>{noteCategory}</div>
        <div
          className={clsx([
            classes.content,
            note.expanded ? '' : classes.collapsed
          ])}
        >
          {note.content}
        </div>
        <NoteComments actionCallback={actionCallback} note={note} />
      </Grid>
      <Grid xs={4} item className={classes.controlContainer}>
        <Grid container className={classes.controls}>
          {note.pinLoading && (
            <span className={classes.pinLoading}>
              <CircularProgress />
            </span>
          )}
          {!note.pinLoading && (
            <span
              className={classes.pin}
              onClick={() =>
                actionCallback(
                  note,
                  note.isPinned
                    ? NOTE_ACTIONS.UNPIN_NOTE
                    : NOTE_ACTIONS.PIN_NOTE
                )
              }
            >
              {note.isPinned ? <PinIconSet /> : <PinIcon />}
            </span>
          )}
          <NoteItemMenu note={note} actionCallback={actionCallback} />
          <span
            className={classes.expandCollapse}
            onClick={() =>
              actionCallback(
                note,
                note.expanded
                  ? NOTE_ACTIONS.COLLAPSE_NOTE_VIEW
                  : NOTE_ACTIONS.EXPAND_NOTE_VIEW
              )
            }
          >
            {note.expanded ? (
              <DDUpIcon stroke="#000" />
            ) : (
              <DDDownIcon stroke="#000" />
            )}
          </span>
        </Grid>
        {note.expanded && (
          <Grid container className={classes.noteProperties}>
            <Grid item xs={6} className={classes.notePropertyName}>
              Status
            </Grid>
            <Grid item xs={6} className={classes.notePropertyValue}>
              {note.status || '--'}
            </Grid>
            <Grid item xs={6} className={classes.notePropertyName}>
              Created by
            </Grid>
            <Grid item xs={6} className={classes.notePropertyValue}>
              {note?.createdBy
                ? `${note.createdBy?.firstName} ${note.createdBy?.lastName}`
                : '--'}
            </Grid>
            <Grid item xs={6} className={classes.notePropertyName}>
              Assigned to
            </Grid>
            <Grid item xs={6} className={classes.notePropertyValue}>
              {note.assignee?.firstName ? `${note.assignee?.firstName}` : ''}
              {note.assignee?.lastName ? `${note.assignee?.lastName}` : ''}
            </Grid>
            <Grid item xs={6} className={classes.notePropertyName}>
              Contacted via
            </Grid>
            <Grid item xs={6} className={classes.notePropertyValue}>
              {note?.contactedVia ? note?.contactedVia : '--'}
            </Grid>
            <Grid item xs={6} className={classes.notePropertyName}>
              Follow up date
            </Grid>
            <Grid item xs={6} className={classes.notePropertyValue}>
              {note?.followupDate
                ? moment(note.followupDate).format('MM/DD/yyyy')
                : '--'}
            </Grid>
            <Grid item xs={6} className={classes.notePropertyName}>
              Follow up time
            </Grid>
            <Grid item xs={6} className={classes.notePropertyValue}>
              {followUpTime}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
